import _ from 'lodash';

import config from '@/data/config';
import themeFallback from '@/data/theme.json';

import store from '@/stores';
import { theme as themeAPI } from '@/services';

export default function useTheme() {
  // states
  const themeKey = 'pd_admin_theme';
  const themeVersionKey = 'pd_admin_theme_version';
  const defaultDomain = process.env.NODE_ENV === 'production'
    ? 'bancoprata.com.br'
    : 'queroprata.com';
  const domainsToForceChange = [
    '192',
    'dev',
    'localhost',
  ];

  // internal
  const isDomainToChange = (value) => {
    let hasToChange = false;
    let reg;

    for (let i = 0; i < domainsToForceChange.length; i++) {
      const domainToCheck = domainsToForceChange[i];

      reg = new RegExp(`${domainToCheck}`, 'g');
      hasToChange = reg.test(value);

      if (hasToChange) break;
    }

    return hasToChange;
  };
  const hexToRGB = (hex) => {
    const rgb = [];
    let mod;
    let a;
    let b;

    for (let i = 0; i < 3; ++i) {
      mod = hex.length === 4 ? 1 : 2;

      a = (i * mod) + 1;
      b = (mod - 1) + a;

      rgb[i] = `0x${hex[a]}${hex[b]}`;
    }

    return `${+rgb[0]},${+rgb[1]},${+rgb[2]}`;
  };
  const setCssVariable = async ({ property, key, value }) => {
    await Promise.resolve(
      document
        .documentElement
        .style
        .setProperty(
          `--${property}-${key}`,
          value,
        ),
    );
  };
  const getLocalTheme = () => ({
    version: localStorage.getItem(themeVersionKey),
    themeLocalStorage: localStorage.getItem(themeKey),
  });
  const checkVersion = () => {
    const { version } = getLocalTheme();
    if (!config.THEME_VERSION) return false;
    return !!version && _.isEqual(version, config.THEME_VERSION);
  };
  const registerCSSVariables = async () => {
    const data = store.state.theme.theme.styles;
    if (!data) return;

    for (const [property, values] of Object.entries(data)) {
      Object
        .entries(values)
        .forEach(async ([key, value], index) => {
          // set colors and logo
          await setCssVariable({ property, key, value });

          if (property === 'color') {
            // set RGB variation
            await setCssVariable({
              property,
              key: `${key}-rgb`,
              value: hexToRGB(value),
            });

            // set loader colors
            await setCssVariable({
              property,
              key: `animation${index + 1}`,
              value,
            });
          }
        });
    }
  };

  // setting theme
  const setTheme = async (payload) => {
    await Promise.resolve(store.dispatch('theme/SET_THEME', payload));
  };
  const fetchTheme = async (domain) => {
    let response = themeFallback;

    try {
      await themeAPI
        .getTheme({ domain })
        .then(({ data }) => {
          response = data.data;
        });
    } catch {
      return Promise.resolve(response);
    }

    return Promise.resolve(response);
  };
  const persistTheme = () => {
    if (config.THEME_VERSION) {
      localStorage.setItem(themeVersionKey, config.THEME_VERSION);
    } else {
      localStorage.removeItem(themeVersionKey);
    }
    localStorage.setItem(themeKey, JSON.stringify(store.state.theme.theme));
  };
  const isValidThemeShape = (candidate) => (
    !!candidate
    && typeof candidate === 'object'
    && !!candidate.business_info
    && !!candidate.styles
  );
  const applyTheme = async (theme, { persist }) => {
    await setTheme({ theme });
    await registerCSSVariables();

    // avoid redundant writes
    if (persist) persistTheme();
  };

  // external
  const domain = () => {
    let { hostname } = window.location;

    if (hostname.endsWith('pdg.dev.local')) return 'pdg.dev.local';

    if (isDomainToChange(hostname)) return defaultDomain;

    hostname = hostname.split('.');
    hostname.shift();
    hostname = hostname.join('.');

    return hostname || defaultDomain;
  };
  const loadTheme = async () => {
    const isSameVersion = checkVersion();
    const domainToFetch = domain();

    const { themeLocalStorage } = getLocalTheme();

    // already loaded in memory for this session
    if (isSameVersion && isValidThemeShape(store.state.theme.theme)) return;

    // try to use cached theme when version matches and shape is valid
    if (isSameVersion && themeLocalStorage) {
      try {
        const candidate = JSON.parse(themeLocalStorage);
        if (isValidThemeShape(candidate)) {
          await applyTheme(candidate, { persist: false });
          return;
        }
      } catch {
        // fall through to fetch
      }
    }

    // cache miss / version mismatch / invalid shape: fetch fresh and persist
    const fresh = await fetchTheme(domainToFetch);
    await applyTheme(fresh, { persist: true });
  };

  return {
    domain,
    loadTheme,
    defaultDomain,
  };
}
